import { useCallback } from 'react';
import useExecutionEnvironment from 'hooks/useExecutionEnvironment';
import { useCreatorContext } from 'controllers/business/Creator/Creator.controller';
import { noop } from 'utils/noop';
import { analytics } from 'utils/analytics';
import { Pack } from 'types';
import { SampleType } from 'types/enums';

export function useAddSessionToCreator(
    sessionGuid?: string,
    interactionSource?: string,
): {
    addToCreator: () => void;
} {
    const { isDAWPlugin } = useExecutionEnvironment();
    const {
        loadSessionByGuid,
        state: {
            samples,
            keyRoot,
            keyQuality,
            keySign,
            bpm,
            instrumentationSessionId,
        },
    } = useCreatorContext();

    const addToCreatorCallback = useCallback(async () => {
        if (sessionGuid) {
            await loadSessionByGuid(sessionGuid, interactionSource);

            samples.forEach((sample) => {
                analytics.trackCreatorUpdated({
                    creatorBpm: bpm,
                    creatorKey: `${keyRoot} ${keyQuality} - ${keySign}`,
                    deletedCreatorSession: false,
                    numberSamplesInCreator: samples.size || 0,
                    sampleAdded: true,
                    sampleRemoved: false,
                    creatorSessionId: instrumentationSessionId,
                    samplePackId: sample.pack.guid,
                    samplePackName: sample.pack.name,
                    assetArtist: sample.pack.artist?.guid,
                    assetBpm: sample.bpm,
                    assetId: sample.guid,
                    assetInstrument: sample.instrumentFamily?.code,
                    assetKey: sample.key?.code,
                    assetLength: sample.duration || undefined,
                    assetName: sample.name,
                    assetGenre: (sample.pack as Pack)?.mainGenre?.code,
                    assetLabel: (sample.pack as Pack)?.label?.guid || undefined,
                    assetLoopType: sample.sampleType as SampleType,
                });
            });
        }
    }, [
        sessionGuid,
        loadSessionByGuid,
        interactionSource,
        samples,
        keyRoot,
        keyQuality,
        keySign,
        bpm,
        instrumentationSessionId,
    ]);

    if (isDAWPlugin || !sessionGuid) {
        return { addToCreator: noop };
    }

    return { addToCreator: addToCreatorCallback };
}
